import { useState } from "react";
import { GCSEOHead, CTABanner, Breadcrumbs, RelatedGuides, LastUpdated } from "./GCLayout";
import { JsonLd } from "../../components/JsonLd";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";

const FAQ_DATA = [
  {
    category: "General",
    questions: [
      {
        q: "What is a Green Card?",
        a: "A Green Card (officially Form I-551) is a document that proves you are a lawful permanent resident (LPR) of the United States. It gives you the right to live and work permanently in the U.S., travel freely in and out of the country, and eventually apply for U.S. citizenship.",
      },
      {
        q: "How long does it take to get a Green Card?",
        a: "It depends on the category. Immediate relatives of U.S. citizens can receive a Green Card in 12-18 months. Employment-based applicants from India or China may wait 5-10+ years due to per-country backlogs. Diversity Visa lottery winners typically complete the process in 12-18 months after selection.",
      },
      {
        q: "Can I work in the U.S. while waiting for my Green Card?",
        a: "Yes. If you filed Form I-485 (Adjustment of Status), you can file Form I-765 to get an Employment Authorization Document (EAD). This allows you to work legally while your Green Card is being processed. Some visa categories (H-1B, L-1) already permit work.",
      },
      {
        q: "What is the difference between a Green Card and U.S. citizenship?",
        a: "A Green Card gives you permanent residency — the right to live and work in the U.S. indefinitely. Citizenship (naturalization) gives you the right to vote, hold public office, travel with a U.S. passport, and cannot be revoked except in extreme cases. You can apply for citizenship after 3-5 years as a permanent resident.",
      },
      {
        q: "Can my Green Card be revoked?",
        a: "Yes. Common reasons include abandoning permanent residency (living outside the U.S. for extended periods), committing certain crimes, immigration fraud, or failing to meet conditions (for conditional Green Cards). Maintaining your status requires living primarily in the U.S.",
      },
    ],
  },
  {
    category: "Application Process",
    questions: [
      {
        q: "What is Adjustment of Status vs. Consular Processing?",
        a: "Adjustment of Status (AOS) is when you apply for a Green Card while already in the U.S. by filing Form I-485. Consular Processing is when you apply at a U.S. Embassy or Consulate in your home country using Form DS-260. AOS is for those legally present in the U.S.; consular processing is for those abroad.",
      },
      {
        q: "What is a priority date and why does it matter?",
        a: "Your priority date is essentially your place in line. For family-based cases, it's the date your I-130 was filed. For employment-based cases, it's usually the date your PERM labor certification was filed. You can only proceed with your Green Card when your priority date is 'current' according to the monthly Visa Bulletin.",
      },
      {
        q: "What is the Visa Bulletin?",
        a: "The Visa Bulletin is published monthly by the Department of State. It shows which priority dates are currently being processed for each Green Card category and country. If your priority date is before the date shown, you can proceed with your application. Check it at travel.state.gov.",
      },
      {
        q: "How much does a Green Card application cost?",
        a: "Total costs vary but typically include: I-485 filing fee ($1,440 for adults), medical exam ($200-$500), passport photos ($15-$30), document translations ($50-$200), and potentially attorney fees ($3,000-$10,000+). Family-based petitions (I-130) cost $535. USCIS fees are subject to change.",
      },
      {
        q: "What happens at the Green Card interview?",
        a: "A USCIS officer reviews your application, verifies documents, asks questions about your background, and makes a decision. For family-based cases, they focus on the legitimacy of the relationship. For employment-based cases, they verify the job offer and qualifications. Bring all original documents.",
      },
    ],
  },
  {
    category: "Family-Based",
    questions: [
      {
        q: "Can my spouse sponsor me for a Green Card?",
        a: "Yes. If your spouse is a U.S. citizen, you're classified as an 'immediate relative' with no annual visa cap and no waiting period for a visa number. If your spouse is a permanent resident (Green Card holder), you fall under the F2A preference category, which may have some wait times.",
      },
      {
        q: "Can I sponsor my parents for a Green Card?",
        a: "Yes, but only if you are a U.S. citizen and at least 21 years old. Parents of U.S. citizens are classified as immediate relatives, meaning there's no wait for a visa number. Green Card holders cannot sponsor parents.",
      },
      {
        q: "What is the Affidavit of Support (I-864)?",
        a: "Form I-864 is a legally binding contract where the sponsor agrees to financially support the immigrant. The sponsor must show income at or above 125% of the Federal Poverty Guidelines. If the sponsor's income is insufficient, a joint sponsor or assets can be used.",
      },
    ],
  },
  {
    category: "Employment-Based",
    questions: [
      {
        q: "Do I need a job offer to get an employment-based Green Card?",
        a: "For most categories (EB-2, EB-3), yes — you need a U.S. employer to sponsor you. However, EB-1A (Extraordinary Ability), EB-1B (Outstanding Researcher), and EB-2 NIW (National Interest Waiver) allow self-petitioning without a specific job offer.",
      },
      {
        q: "What is PERM Labor Certification?",
        a: "PERM is a process where your employer must prove to the Department of Labor that there are no qualified U.S. workers available for the position. It involves advertising the job, reviewing applicants, and filing ETA Form 9089. It typically takes 8-18 months.",
      },
      {
        q: "Can I change jobs while my Green Card is being processed?",
        a: "It's complicated. Under AC21 portability, if your I-485 has been pending for 180+ days, you can change to a 'same or similar' job without restarting the process. However, changing jobs before the 180-day mark can jeopardize your application.",
      },
    ],
  },
  {
    category: "After Getting Your Green Card",
    questions: [
      {
        q: "How long is a Green Card valid?",
        a: "A standard Green Card is valid for 10 years. A conditional Green Card (for spouses married less than 2 years or EB-5 investors) is valid for 2 years. You must renew or remove conditions before expiration.",
      },
      {
        q: "When can I apply for U.S. citizenship?",
        a: "Generally, after 5 years as a permanent resident (3 years if married to a U.S. citizen). You must be physically present in the U.S. for at least 30 months of the 5-year period, have continuous residence, and demonstrate good moral character.",
      },
      {
        q: "Can I travel outside the U.S. with a Green Card?",
        a: "Yes, but extended absences (over 6 months) can raise questions about whether you've abandoned your residency. Trips over 1 year without a re-entry permit will cause you to lose your Green Card. Get a re-entry permit (Form I-131) if you plan to be away for more than a year.",
      },
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
        data-testid={`faq-q-${q.slice(0, 30).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        <span className="font-medium text-sm text-slate-800">{q}</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
          {a}
        </div>
      )}
    </div>
  );
}

export default function GCFAQPage() {
  const totalQuestions = FAQ_DATA.reduce((acc, s) => acc + s.questions.length, 0);

  return (
    <>
      <GCSEOHead
        title="Green Card FAQ 2026: 19 Common Questions Answered"
        description="19 clear answers to frequently asked questions about the U.S. Green Card process - eligibility, costs, timelines, interviews, and life after approval. Based on official USCIS information."
        path="/faq"
      />

      {/* Breadcrumbs */}
      <Breadcrumbs current="FAQ" />

      {/* Header */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4" data-testid="gc-faq-h1">
            Green Card FAQ
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl">
            {totalQuestions} clear answers to the most common questions about the U.S. Green Card process —
            from basic eligibility to what happens after you receive your card. No legal jargon, just
            plain English based on official USCIS information.
          </p>
          <div className="mt-4">
            <LastUpdated />
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-12 md:py-16" data-testid="gc-faq-sections">
        <div className="max-w-4xl mx-auto px-4 space-y-10">
          {FAQ_DATA.map((section) => (
            <div key={section.category}>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 font-heading mb-4">
                {section.category}
              </h2>
              <div className="space-y-2">
                {section.questions.map((faq) => (
                  <FAQItem key={faq.q} q={faq.q} a={faq.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* JSON-LD FAQ Schema */}
      <JsonLd data={{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ_DATA.flatMap((s) =>
              s.questions.map((faq) => ({
                "@type": "Question",
                name: faq.q,
                acceptedAnswer: { "@type": "Answer", text: faq.a },
              }))
            ),
          }} />

      {/* Soft CTA */}
      <section className="py-10 bg-slate-50 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-slate-600 mb-4 text-base">
            Have a question specific to your situation?
          </p>
          <a
            href="https://immigrationguide.us/auth?mode=register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors"
            data-testid="gc-faq-cta"
          >
            Ask our AI assistant for free <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* DV Lottery Resources */}
      <section className="py-10 border-t border-slate-100" data-testid="gc-faq-dv-resources">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-lg font-bold text-slate-900 font-heading mb-4">DV Lottery Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href="https://immigrationguide.us/how-to-check-dv-lottery-results" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">How to Check DV Lottery Results</h3>
              <p className="text-xs text-slate-500">Official steps to verify your selection status</p>
            </a>
            <a href="https://immigrationguide.us/dv-lottery-eligibility" target="_blank" rel="noopener noreferrer" className="group p-4 rounded-lg border border-slate-200 hover:border-emerald-200 hover:shadow-sm transition-all">
              <h3 className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700 transition-colors mb-1">DV Lottery Eligibility</h3>
              <p className="text-xs text-slate-500">Country, education, and work experience requirements</p>
            </a>
          </div>
        </div>
      </section>

      <RelatedGuides currentPath="/faq" />
      <CTABanner />
    </>
  );
}
